import { useDispatch } from "react-redux";
import { useCallback } from "react";

export const useAction = <A extends Array<unknown>>(
  dispatchAction: (...args: A) => void,
  callbackAfterDispatch?: VoidFunction,
): ((...args: A) => void) => {
  const dispatch = useDispatch();

  return useCallback(
    (...args: A) => {
      dispatch(dispatchAction(...args));
      callbackAfterDispatch && callbackAfterDispatch();
    },
    [dispatch, dispatchAction],
  );
};

/**
 * use redux action with prepare payload function
 *
 * @example
 * const setApple = useCustomizeAction(setFruit, () => "apple") // setApple is VoidFunction
 */
export const useCustomizeAction = <I extends unknown, P extends unknown>(
  dispatchAction: (payload: P) => void,
  preparePayload: (input: I) => NoInfer<P>,
  deps: unknown[] = [],
): ((input: NoInfer<I>) => void) => {
  const dispatch = useDispatch();

  return useCallback(
    (input: I) => {
      const payload = preparePayload(input);

      dispatch(dispatchAction(payload));
    },
    [dispatch, dispatchAction, ...deps],
  );
};

/**
 * use redux action with payload arguments
 *
 * @example
 * const setApple = useActionWithArgs(setFruit, "apple") // setApple is VoidFunction
 */
export function useActionWithArgs<P extends void | undefined>(
  dispatchAction: (payload: P) => void,
  payload?: NoInfer<P>,
): VoidFunction;
export function useActionWithArgs<P extends unknown>(
  dispatchAction: (payload: P) => void,
  payload: NoInfer<P>,
): VoidFunction;
export function useActionWithArgs<P>(dispatchAction: (payload: P) => void, payload: NoInfer<P>): VoidFunction {
  return useCustomizeAction<void, P>(dispatchAction, () => payload, [payload]);
}
